// Plan 08-06 — Tutorial completion + skip + hint-shown persistence.
//
// Single-source of tutorial-state truth. Backed by localStorage via
// nanostores's persistentAtom (storage key `runegambit.tutorial`).
//
// Read by:
//   - <SplashScreen /> to decide whether to show "I've played before" CTA.
//   - <CoachOverlay />'s shouldShowHint() gating (post-tutorial matches 2-4).
//   - <TutorialScreen /> to short-circuit if user already completed.

import { persistentAtom } from '@nanostores/persistent'

export interface TutorialState {
  completed: boolean
  hintsShown: ReadonlyArray<string>
  skipped: boolean
}

const TUTORIAL_STORAGE_KEY = 'runegambit.tutorial'

const DEFAULT_STATE: TutorialState = Object.freeze({
  completed: false,
  hintsShown: Object.freeze([]) as ReadonlyArray<string>,
  skipped: false,
})

export const $tutorial = persistentAtom<TutorialState>(
  TUTORIAL_STORAGE_KEY,
  DEFAULT_STATE,
  {
    encode: (v) => JSON.stringify(v),
    decode: (raw) => {
      if (!raw) return DEFAULT_STATE
      try {
        const parsed = JSON.parse(raw) as Partial<TutorialState>
        return {
          completed: parsed.completed ?? false,
          hintsShown: Array.isArray(parsed.hintsShown) ? parsed.hintsShown : [],
          skipped: parsed.skipped ?? false,
        }
      } catch {
        return DEFAULT_STATE
      }
    },
  },
)

export function markTutorialComplete(): void {
  const cur = $tutorial.get()
  $tutorial.set({ ...cur, completed: true })
}

export function markTutorialSkipped(): void {
  const cur = $tutorial.get()
  $tutorial.set({ ...cur, skipped: true })
}

/** Append a hint key to hintsShown if not already present (one-shot). */
export function incrementHint(key: string): void {
  const cur = $tutorial.get()
  if (cur.hintsShown.includes(key)) return
  $tutorial.set({ ...cur, hintsShown: [...cur.hintsShown, key] })
}

export function resetTutorialState(): void {
  $tutorial.set(DEFAULT_STATE)
}
